/**
 * WebRewardsPageViewModel - Web Platform Rewards ViewModel
 *
 * Handles all rewards page business logic including:
 * - Dollar vs Item redemption type handling
 * - Dynamic tier generation for dollar redemption
 * - Redemption validation (points, max per order, coupon conflicts)
 * - Button state management
 *
 * Note: Data fetching is handled by useRewardsData hook in App.tsx.
 * This ViewModel uses the already-loaded store data.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRewardsStore, useCartStore, useRestaurantStore } from '@restaurant/core'
import type {
  MerchantItemRedemptionGroup,
  MerchantItemRedemption,
  RewardPointGroupData,
  MenuItem,
} from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


// Reward click result — lets the component know if a conflict needs user attention
export type RewardClickResult =
  | { blocked: false }
  | { blocked: true; reason: 'coupon_conflict' }

// Redemption state types
export type RedemptionState = 'available' | 'added' | 'insufficient'

export interface RedemptionStateResult {
  state: RedemptionState
  pointsNeeded: number
}

export interface RedemptionValidation {
  canRedeem: boolean
  reason?: 'insufficient_points' | 'max_redemptions' | 'coupon_conflict'
  message?: string
}

// Metadata stored separately to avoid passing custom props to DOM
interface RewardMetadata {
  groupId: string
  pointValue: number
  itemId?: string
  redemptionState: RedemptionState
}

/**
 * Web Rewards Page ViewModel
 * Provides all state and actions for the RewardsPage component
 *
 * Data is loaded by useRewardsData() in App.tsx - this ViewModel
 * operates on the already-loaded store data.
 */
// State for the product detail sheet when a reward item has required customizations
export interface PendingRewardProduct {
  menuItem: MenuItem
  groupId: string
  pointValue: number
  itemId: string
}

export function useWebRewardsPageViewModel() {
  // Stores - data is already loaded by useRewardsData in App.tsx
  const {
    merchantConfig,
    balance,
    appliedRewardDetails,
    addPendingRedemption,
    removePendingRedemption,
  } = useRewardsStore()

  const { appliedOfferId } = useCartStore()
  const getMenuItemById = useRestaurantStore((s) => s.getMenuItemById)

  // Metadata ref for click handling (avoids passing custom props to DOM)
  const metadataRef = useRef<Map<string, RewardMetadata>>(new Map())

  // Product detail sheet state for reward items with required customizations
  const [pendingRewardProduct, setPendingRewardProduct] = useState<PendingRewardProduct | null>(null)

  // ============================================
  // Redemption State Helpers
  // ============================================

  const getRemainingPoints = useCallback(() => {
    const pendingPoints = appliedRewardDetails.reduce(
      (sum: number, r: { pointValue: number }) => sum + r.pointValue,
      0
    )
    return balance - pendingPoints
  }, [balance, appliedRewardDetails])

  const isGroupRedeemed = useCallback(
    (groupId: string) => {
      return appliedRewardDetails.some((r: { groupId: string }) => r.groupId === groupId)
    },
    [appliedRewardDetails]
  )

  const canAffordReward = useCallback(
    (pointValue: number) => {
      return getRemainingPoints() >= pointValue
    },
    [getRemainingPoints]
  )

  const canRedeemMore = useCallback(() => {
    const maxRedemptions = merchantConfig?.max_redemptions_per_order
    if (maxRedemptions === undefined || maxRedemptions === null) {
      return true
    }
    return appliedRewardDetails.length < maxRedemptions
  }, [merchantConfig, appliedRewardDetails])

  const getRedemptionState = useCallback(
    (groupId: string, pointValue: number): RedemptionStateResult => {
      if (isGroupRedeemed(groupId)) {
        return { state: 'added', pointsNeeded: 0 }
      }
      if (!canAffordReward(pointValue)) {
        const pointsNeeded = pointValue - getRemainingPoints()
        return { state: 'insufficient', pointsNeeded }
      }
      return { state: 'available', pointsNeeded: 0 }
    },
    [isGroupRedeemed, canAffordReward, getRemainingPoints]
  )

  const getButtonText = useCallback((state: RedemptionStateResult, pointValue?: number): string => {
    switch (state.state) {
      case 'added':
        return 'Redeemed'
      case 'insufficient':
        return `${state.pointsNeeded} More Points`
      case 'available':
      default:
        return pointValue ? `Redeem ${pointValue} Points` : 'Redeem'
    }
  }, [])

  // ============================================
  // Redemption Validation
  // ============================================

  const validateRedemption = useCallback(
    (pointValue: number): RedemptionValidation => {
      if (!canAffordReward(pointValue)) {
        return {
          canRedeem: false,
          reason: 'insufficient_points',
          message: `You need ${pointValue - getRemainingPoints()} more points`,
        }
      }

      if (!canRedeemMore()) {
        const maxRedemptions = merchantConfig?.max_redemptions_per_order || 1
        return {
          canRedeem: false,
          reason: 'max_redemptions',
          message: `Maximum ${maxRedemptions} reward${maxRedemptions > 1 ? 's' : ''} per order`,
        }
      }

      const rewardAndCouponAllowed = merchantConfig?.reward_and_coupon_allowed ?? false
      if (!rewardAndCouponAllowed && appliedOfferId != null) {
        return {
          canRedeem: false,
          reason: 'coupon_conflict',
          message: 'Cannot combine rewards with coupons',
        }
      }

      return { canRedeem: true }
    },
    [canAffordReward, canRedeemMore, merchantConfig, appliedOfferId, getRemainingPoints]
  )

  // ============================================
  // Redemption Actions
  // ============================================

  const handleRedemption = useCallback(
    (groupId: string, pointValue: number, itemId?: string): RedemptionValidation => {
      // Check if already redeemed - toggle off
      if (isGroupRedeemed(groupId)) {
        removePendingRedemption(groupId)

        // Remove the reward item from cart (item-based only)
        if (itemId && merchantConfig?.redemption_type !== 'dollar') {
          const groups = merchantConfig?.item_redemption_groups || []
          const group = groups.find((g) => g.id === groupId)
          const rewardItem = group?.items.find((i) => i.id === itemId)
          const menuItemId = rewardItem?.menu_item?.id || itemId

          const cartState = useCartStore.getState()
          const rewardCartItem = cartState.items.find(
            (i) => i.isRewardRedemption && i.menuItem.id === menuItemId
          )
          if (rewardCartItem) {
            cartState.removeItem(rewardCartItem.id)
          }
        }

        return { canRedeem: true }
      }

      // Validate redemption
      const validation = validateRedemption(pointValue)
      if (!validation.canRedeem) {
        if (validation.reason === 'coupon_conflict') {
          return validation // Let the component handle the conflict
        }
        return validation
      }

      // For item-based redemption, check if item has required customizations
      if (itemId && merchantConfig?.redemption_type !== 'dollar') {
        const groups = merchantConfig?.item_redemption_groups || []
        const group = groups.find((g) => g.id === groupId)
        const rewardItem = group?.items.find((i) => i.id === itemId)
        const menuItemId = rewardItem?.menu_item?.id || itemId
        const fullMenuItem = getMenuItemById(menuItemId)

        const hasRequiredCustomizations = fullMenuItem?.customizations?.some(
          (c) => c.isRequired
        )

        if (hasRequiredCustomizations && fullMenuItem) {
          // Defer to product detail sheet — don't add to cart yet
          // Apply redemption in rewards store so the button shows "REDEEMED"
          addPendingRedemption(pointValue, groupId, itemId)
          setPendingRewardProduct({ menuItem: fullMenuItem, groupId, pointValue, itemId })
          return { canRedeem: true }
        }
      }

      // Apply redemption in rewards store
      addPendingRedemption(pointValue, groupId, itemId)

      // For item-based redemption, add the reward item to cart (no required customizations)
      if (itemId && merchantConfig?.redemption_type !== 'dollar') {
        addRewardItemToCart(groupId, pointValue, itemId)
      }

      return { canRedeem: true }
    },
    [isGroupRedeemed, validateRedemption, addPendingRedemption, removePendingRedemption, merchantConfig, getMenuItemById]
  )

  // ============================================
  // Cart Helpers
  // ============================================

  const addRewardItemToCart = useCallback(
    (groupId: string, pointValue: number, itemId: string, customizationSelections: Array<{ customizationItemId: string; selectedOptionId: string }> = []) => {
      const groups = merchantConfig?.item_redemption_groups || []
      const group = groups.find((g) => g.id === groupId)
      const rewardItem = group?.items.find((i) => i.id === itemId)

      if (rewardItem?.menu_item) {
        const menuItemId = rewardItem.menu_item.id || itemId
        const fullMenuItem = getMenuItemById(menuItemId)
        const menuItem = rewardItem.menu_item

        useCartStore.getState().addItem(
          fullMenuItem || ({
            id: menuItemId,
            name: menuItem.name || group?.reward_name || 'Reward Item',
            price: 0,
            // The reward group's menu_item only carries { id, name, price, image_url };
            // it has no description/clover_id, so these fall back to empty strings
            // (matching the prior `menuItem.description || ''` runtime behavior).
            description: '',
            cloverId: '',
            categoryId: '',
            customizations: [],
            priceVariants: {},
            imageUrl: menuItem.image_url,
          } as unknown as MenuItem),
          1,
          customizationSelections,
          { selectedPriceVariant: 'base' }
        )

        // Mark the just-added item as a reward redemption
        const cartState = useCartStore.getState()
        const addedItem = cartState.items[cartState.items.length - 1]
        if (addedItem) {
          cartState.updateItem(addedItem.id, {
            isRewardRedemption: true,
            rewardPointsCost: pointValue,
            totalPrice: 0,
          })
        }
      }
    },
    [merchantConfig, getMenuItemById]
  )

  // Confirm reward from product detail sheet (after customization selection)
  const confirmRewardProduct = useCallback(
    (customizationSelections: Array<{ customizationItemId: string; selectedOptionId: string }>) => {
      if (!pendingRewardProduct) return

      const { groupId, pointValue, itemId } = pendingRewardProduct
      addRewardItemToCart(groupId, pointValue, itemId, customizationSelections)
      setPendingRewardProduct(null)
    },
    [pendingRewardProduct, addRewardItemToCart]
  )

  // Cancel reward product detail — undo the pending redemption
  const cancelRewardProduct = useCallback(() => {
    if (pendingRewardProduct) {
      removePendingRedemption(pendingRewardProduct.groupId)
    }
    setPendingRewardProduct(null)
  }, [pendingRewardProduct, removePendingRedemption])

  // ============================================
  // Reward Groups Generation
  // ============================================

  const rewardGroups = useMemo((): RewardPointGroupData[] => {
    if (!merchantConfig) {
      return []
    }

    // Clear and rebuild metadata map
    metadataRef.current.clear()

    // Check for dollar vs item redemption
    const hasDollarRedemption =
      merchantConfig.redemption_type === 'dollar' &&
      merchantConfig.point_value &&
      merchantConfig.dollar_value
    const hasItemRedemption =
      merchantConfig.item_redemption_groups && merchantConfig.item_redemption_groups.length > 0

    // Handle dollar-based redemption: generate tiers dynamically based on balance
    if (hasDollarRedemption && !hasItemRedemption) {
      const basePointValue = merchantConfig.point_value!
      const baseDollarValue = merchantConfig.dollar_value!
      const remainingPoints = balance

      // Calculate how many tiers user can afford + 1
      const affordableTiers = Math.floor(remainingPoints / basePointValue)
      const numTiers = Math.max(1, affordableTiers + 1)

      const groups: RewardPointGroupData[] = []

      for (let i = 1; i <= numTiers; i++) {
        const tierPoints = basePointValue * i
        const tierDollars = baseDollarValue * i
        const groupId = `dollar-tier-${i}`
        const state = getRedemptionState(groupId, tierPoints)

        // Store metadata
        metadataRef.current.set(`${i - 1}-0`, {
          groupId,
          pointValue: tierPoints,
          itemId: undefined,
          redemptionState: state.state,
        })

        groups.push({
          id: groupId,
          groupName: `${tierPoints} Points`,
          rewards: [
            {
              rewardName: `$${tierDollars.toFixed(2)} Reward`,
              rewardImageUrl: undefined,
              showRewardImage: false,
              buttonText: getButtonText(state, tierPoints),
              disabled: state.state === 'insufficient',
              isEarned: state.state === 'available' || state.state === 'added',
            },
          ],
        })
      }

      return groups
    }

    // Handle item-based redemption: use server groups directly
    if (!merchantConfig.item_redemption_groups) {
      return []
    }

    return merchantConfig.item_redemption_groups
      .sort(
        (a: MerchantItemRedemptionGroup, b: MerchantItemRedemptionGroup) =>
          a.display_order - b.display_order
      )
      .map((group: MerchantItemRedemptionGroup, groupIndex: number) => {
        const state = getRedemptionState(group.id, group.point_value)

        const rewards =
          group.items.length > 0
            ? group.items.map((item: MerchantItemRedemption, rewardIndex: number) => {
                metadataRef.current.set(`${groupIndex}-${rewardIndex}`, {
                  groupId: group.id,
                  pointValue: group.point_value,
                  itemId: item.id,
                  redemptionState: state.state,
                })

                return {
                  rewardName: item.menu_item?.name || group.reward_name,
                  rewardImageUrl: group.image_url || item.menu_item?.image_url,
                  buttonText: getButtonText(state, group.point_value),
                  disabled: state.state === 'insufficient',
                  isEarned: state.state === 'available' || state.state === 'added',
                }
              })
            : [
                (() => {
                  metadataRef.current.set(`${groupIndex}-0`, {
                    groupId: group.id,
                    pointValue: group.point_value,
                    itemId: undefined,
                    redemptionState: state.state,
                  })

                  return {
                    rewardName: group.reward_name,
                    rewardImageUrl: group.image_url,
                    buttonText: getButtonText(state, group.point_value),
                    disabled: state.state === 'insufficient',
                    isEarned: state.state === 'available' || state.state === 'added',
                  }
                })(),
              ]

        return {
          id: group.id,
          groupName: group.reward_name || `${group.point_value} Points`,
          rewards,
        }
      })
  }, [merchantConfig, balance, getRedemptionState, getButtonText])

  // ============================================
  // Click Handler
  // ============================================

  const handleRewardClick = useCallback(
    (groupIndex: number, rewardIndex: number): RewardClickResult => {
      const metadata = metadataRef.current.get(`${groupIndex}-${rewardIndex}`)
      if (!metadata) {
        safeGetLogger()?.warn('No metadata found for reward', { domain: 'reward', groupIndex, rewardIndex })
        return { blocked: false }
      }

      const result = handleRedemption(metadata.groupId, metadata.pointValue, metadata.itemId)

      if (!result.canRedeem && result.reason === 'coupon_conflict') {
        return { blocked: true, reason: 'coupon_conflict' }
      }

      if (!result.canRedeem && result.message) {
        safeGetLogger()?.debug('Reward redemption blocked', { domain: 'reward', message: result.message })
      }

      return { blocked: false }
    },
    [handleRedemption]
  )

  // ============================================
  // Reconcile: restore pending redemptions from persisted cart on load
  // ============================================

  const hasReconciledRef = useRef(false)

  useEffect(() => {
    if (hasReconciledRef.current) return
    if (!merchantConfig || merchantConfig.redemption_type === 'dollar') return
    if (!merchantConfig.item_redemption_groups?.length) return

    const currentCartItems = useCartStore.getState().items
    const rewardCartItems = currentCartItems.filter((i) => i.isRewardRedemption)
    if (rewardCartItems.length === 0) return

    hasReconciledRef.current = true

    const groups = merchantConfig.item_redemption_groups
    for (const cartItem of rewardCartItems) {
      // Reverse-lookup: find the group/item that matches this cart item's menu item ID
      for (const group of groups) {
        const matchingItem = group.items.find(
          (i) => i.menu_item?.id === cartItem.menuItem.id
        )
        if (matchingItem && !isGroupRedeemed(group.id)) {
          addPendingRedemption(group.point_value, group.id, matchingItem.id)
          break
        }
      }
    }
  }, [merchantConfig, isGroupRedeemed, addPendingRedemption])

  // ============================================
  // Sync: clear pending redemptions when reward items are removed from cart
  // ============================================

  const cartItems = useCartStore((s) => s.items)
  const prevRewardCartItemsRef = useRef<Set<string>>(new Set())

  // Track which reward menu item IDs are currently in the cart
  const currentRewardCartItemIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of cartItems) {
      if (item.isRewardRedemption) {
        ids.add(item.menuItem.id)
      }
    }
    return ids
  }, [cartItems])

  useEffect(() => {
    const prevIds = prevRewardCartItemsRef.current
    prevRewardCartItemsRef.current = currentRewardCartItemIds

    // Only act when a reward item was REMOVED (was in prev, not in current)
    if (prevIds.size === 0) return
    if (merchantConfig?.redemption_type === 'dollar') return
    if (appliedRewardDetails.length === 0) return

    const groups = merchantConfig?.item_redemption_groups || []
    for (const reward of appliedRewardDetails) {
      if (!reward.itemId) continue

      const group = groups.find((g) => g.id === reward.groupId)
      const rewardItem = group?.items.find((i) => i.id === reward.itemId)
      const menuItemId = rewardItem?.menu_item?.id || reward.itemId

      const wasInCart = prevIds.has(menuItemId)
      const stillInCart = currentRewardCartItemIds.has(menuItemId)

      if (wasInCart && !stillInCart) {
        removePendingRedemption(reward.groupId)
      }
    }
  }, [currentRewardCartItemIds, appliedRewardDetails, merchantConfig, removePendingRedemption])

  // ============================================
  // Computed Properties
  // ============================================

  const hasDollarRedemption =
    merchantConfig?.redemption_type === 'dollar' &&
    merchantConfig?.point_value &&
    merchantConfig?.dollar_value

  const hasItemRedemption =
    merchantConfig?.item_redemption_groups && merchantConfig.item_redemption_groups.length > 0

  const hasRewards = Boolean(hasDollarRedemption || hasItemRedemption)

  const isEmpty = !hasRewards || rewardGroups.length === 0

  // ============================================
  // Return ViewModel Interface
  // ============================================

  return {
    // State (data loaded by useRewardsData in App.tsx)
    balance,
    merchantConfig,
    rewardGroups,
    hasRewards,
    isEmpty,

    // Product detail sheet state (for reward items with required customizations)
    pendingRewardProduct,
    confirmRewardProduct,
    cancelRewardProduct,

    // Actions
    handleRewardClick,
    handleRedemption,

    // Helpers
    getRemainingPoints,
    getRedemptionState,
    getButtonText,
    validateRedemption,
    canAffordReward,
    canRedeemMore,
    isGroupRedeemed,
  }
}

// Also export with the old name for backwards compatibility
export { useWebRewardsPageViewModel as useRewardsPageViewModel }
